/**
 * SF-4651: past demo builds (status, who started it, when, and stage or production) and the way
 * into the wizard. SF-4746: "My demos" (sf-api scope=mine) for people who create demos; "All demos"
 * (scope=all, every person's builds, filtered by person and status) only with "See all prospect
 * demos" (admin:demos:read_all). Dismissed builds are left out unless "Show dismissed" is on.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCan, usePermissions } from '@refinedev/core';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Result,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Typography,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { EnvironmentTag } from './components/EnvironmentBadge';
import {
  demoErrorMessage,
  useDemoBuilds,
  type DemoBuildListItem,
  type DemoBuildStatus,
} from './api/demoBuildsClient';
import { StatusTag } from './components/StatusTag';
import {
  DEMO_BUILD_STATUS_LABELS,
  DEMOS_PERMISSION,
  DEMOS_READ_ALL_PERMISSION,
  demoDetailPath,
  startedBy,
} from './constants';
import { hasPermission } from '../../utils/permissions';

const PAGE_SIZE = 20;

type View = 'mine' | 'all';

const STATUS_OPTIONS = Object.entries(DEMO_BUILD_STATUS_LABELS).map(([value, { label }]) => ({
  value: value as DemoBuildStatus,
  label,
}));

const formatWhen = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

export const DemosListPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: can, isLoading: canLoading } = useCan({ resource: 'demos', action: 'list' });
  const { data: permissions, isLoading: permissionsLoading } = usePermissions<string[]>();
  const canCreate = hasPermission(permissions ?? [], DEMOS_PERMISSION);
  const canReadAll = hasPermission(permissions ?? [], DEMOS_READ_ALL_PERMISSION);
  const [chosenView, setChosenView] = useState<View>('mine');
  // Someone who can only read opens on All demos; nobody gets a tab they cannot use.
  const view: View = canReadAll && (chosenView === 'all' || !canCreate) ? 'all' : 'mine';
  const [page, setPage] = useState(1);
  const [showDismissed, setShowDismissed] = useState(false);
  const [person, setPerson] = useState<string | undefined>();
  const [status, setStatus] = useState<DemoBuildStatus | undefined>();
  // The person filter offers everyone seen on the All demos pages loaded so far.
  const [people, setPeople] = useState<Record<string, string>>({});

  const allowed = can?.can === true;
  const query = useDemoBuilds(
    {
      scope: view,
      include_dismissed: showDismissed,
      ...(view === 'all' && person ? { created_by: person } : {}),
      ...(view === 'all' && status ? { status } : {}),
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    },
    allowed && !permissionsLoading,
  );
  const list = query.data?.data;

  useEffect(() => {
    if (view !== 'all' || !list?.items?.length) return;
    setPeople((seen) => ({
      ...seen,
      ...Object.fromEntries(
        list.items.map((row) => [row.created_by.id, startedBy(row.created_by)]),
      ),
    }));
  }, [view, list]);

  if (canLoading || permissionsLoading) return <Spin />;
  if (!allowed) {
    return <Result status="403" title="403" subTitle="You do not have permission to see demos." />;
  }

  /** Any change to what is listed starts again from the first page. */
  const firstPage = () => setPage(1);
  const tabs = [
    ...(canCreate ? [{ key: 'mine', label: 'My demos' }] : []),
    ...(canReadAll ? [{ key: 'all', label: 'All demos' }] : []),
  ];
  const failedText = view === 'all' ? 'Could not load the demos' : 'Could not load your demos';

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card
        title="Demos"
        extra={
          canCreate && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/demos/new')}>
              New demo
            </Button>
          )
        }
      >
        <Tabs
          activeKey={view}
          items={tabs}
          onChange={(key) => {
            setChosenView(key as View);
            firstPage();
          }}
        />
        <Space wrap style={{ marginBottom: 16 }}>
          {view === 'all' && (
            <>
              <Select
                aria-label="Started by"
                placeholder="Everyone"
                allowClear
                style={{ minWidth: 200 }}
                value={person}
                onChange={(value?: string) => {
                  setPerson(value);
                  firstPage();
                }}
                options={Object.entries(people).map(([value, label]) => ({ value, label }))}
              />
              <Select
                aria-label="Status"
                placeholder="Any status"
                allowClear
                style={{ minWidth: 180 }}
                value={status}
                onChange={(value?: DemoBuildStatus) => {
                  setStatus(value);
                  firstPage();
                }}
                options={STATUS_OPTIONS}
              />
            </>
          )}
          <Checkbox
            checked={showDismissed}
            onChange={(e) => {
              setShowDismissed(e.target.checked);
              firstPage();
            }}
          >
            Show dismissed
          </Checkbox>
        </Space>
        {query.isError && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message={demoErrorMessage(query.error, `${failedText}.`)}
          />
        )}
        <Table<DemoBuildListItem>
          rowKey="id"
          // The last list stays while the next one loads (another tab, filter or page): it shows
          // as loading so its rows are not taken for the new list.
          loading={(query.isLoading && query.fetchStatus !== 'idle') || query.isPreviousData}
          dataSource={list?.items ?? []}
          locale={{ emptyText: query.isError ? failedText : 'No demos yet' }}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total: list?.total ?? 0,
            showSizeChanger: false,
            onChange: setPage,
          }}
          onRow={(row) => ({
            onClick: () => navigate(demoDetailPath(row.id)),
            style: { cursor: 'pointer' },
          })}
          columns={[
            {
              title: 'Prospect',
              dataIndex: 'domain',
              render: (domain: string | null, row) => (
                <Space direction="vertical" size={0}>
                  <Typography.Text strong>{domain ?? '—'}</Typography.Text>
                  {row.area_label && (
                    <Typography.Text type="secondary">{row.area_label}</Typography.Text>
                  )}
                </Space>
              ),
            },
            {
              title: 'Status',
              dataIndex: 'status',
              render: (s: string, row) => <StatusTag status={s} dismissed={!!row.dismissed_at} />,
            },
            {
              title: 'Environment',
              dataIndex: 'environment',
              render: (env: string) => <EnvironmentTag env={env} />,
            },
            {
              title: 'Started by',
              key: 'who',
              render: (_: unknown, row) => startedBy(row.created_by),
            },
            { title: 'Started', dataIndex: 'created_at', render: formatWhen },
          ]}
        />
      </Card>
    </Space>
  );
};
